/**
 * ProfileReminderPopup — friendly modal shown to users who have missing
 * profile fields (date_of_birth, gender, blood_group, marital_status,
 * emergency_contact_name, emergency_contact_number).
 *
 * Behaviour:
 *  • Only renders when at least one field is missing.
 *  • Lists ONLY the missing fields, grouped by section.
 *  • "Complete Now"    → navigates to /profile, then closes.
 *  • "Remind Me Later" → snoozes until tomorrow (localStorage per-user per-date).
 *  • Once all fields are filled the caller never mounts this component.
 *  • Backdrop does NOT close on click (prevents accidental dismissal).
 */
import React, { useEffect, useRef } from "react";
import useOverlayScrollLock from "@/hooks/useOverlayScrollLock";
import { useNavigate } from "react-router-dom";
import { X, Calendar, User2, Droplets, Heart, AlertTriangle, Phone } from "lucide-react";

/* ── field definitions ───────────────────────────────────────────────────────
   Each entry describes one required field. `key` matches the user object prop. */
const PERSONAL_FIELDS = [
  { key: "date_of_birth",  label: "Date of Birth",  placeholder: "dd/mm/yyyy",         icon: Calendar },
  { key: "gender",         label: "Gender",          placeholder: "Select…",             icon: User2 },
  { key: "blood_group",    label: "Blood Group",     placeholder: "Select…",             icon: Droplets },
  { key: "marital_status", label: "Marital Status",  placeholder: "Select…",             icon: Heart },
];

const EMERGENCY_FIELDS = [
  { key: "emergency_contact_name",   label: "Contact Name",   placeholder: "Full Name",      icon: AlertTriangle },
  { key: "emergency_contact_number", label: "Contact Number", placeholder: "Mobile Number",  icon: Phone },
];

/* ── CSS injected once ──────────────────────────────────────────────────────── */
const STYLE_ID = "flg-profile-reminder-style";
function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    @keyframes flg-pr-slide-in {
      0%   { transform: translateY(24px) scale(0.97); opacity: 0; }
      100% { transform: translateY(0)    scale(1);    opacity: 1; }
    }
    .flg-pr-overlay {
      position: fixed; inset: 0; z-index: 9990;
      background: rgba(10, 14, 26, 0.55);
      backdrop-filter: blur(2px);
      display: flex; align-items: center; justify-content: center;
      padding: 16px;
    }
    .flg-pr-card {
      background: var(--flg-bg-surface, #fff);
      border-radius: 16px;
      box-shadow: 0 24px 72px rgba(0,0,0,0.22), 0 2px 8px rgba(0,0,0,0.08);
      max-width: 488px; width: 100%;
      max-height: calc(100dvh - 32px);
      overflow-y: auto;
      animation: flg-pr-slide-in 0.3s cubic-bezier(0.22,1,0.36,1) both;
    }
    .flg-pr-header {
      padding: 24px 24px 0;
      display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
    }
    .flg-pr-body { padding: 0 24px 8px; }
    .flg-pr-footer {
      padding: 12px 24px 20px;
      display: flex; gap: 10px; justify-content: flex-end;
      border-top: 1px solid var(--flg-border, #e5e7eb);
      margin-top: 4px;
    }
    .flg-pr-section-label {
      font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--flg-fg-muted, #6b7280);
      margin: 16px 0 8px;
      display: flex; align-items: center; gap: 6px;
    }
    .flg-pr-field-row {
      display: flex; align-items: center; gap: 10px;
      padding: 9px 12px;
      border-radius: 8px;
      background: var(--flg-bg-subtle, #f9fafb);
      border: 1px solid var(--flg-border, #e5e7eb);
      margin-bottom: 6px;
    }
    .flg-pr-field-row:last-child { margin-bottom: 0; }
    .flg-pr-field-icon {
      color: var(--flg-fg-muted, #9ca3af);
      flex-shrink: 0;
    }
    .flg-pr-field-label {
      font-size: 13px; font-weight: 600;
      color: var(--flg-fg, #111827);
      flex: 1; min-width: 0;
    }
    .flg-pr-field-placeholder {
      font-size: 12px;
      color: var(--flg-fg-muted, #9ca3af);
      white-space: nowrap;
    }
    .flg-pr-dismiss-btn {
      background: none; border: none; cursor: pointer; padding: 4px;
      border-radius: 6px; color: var(--flg-fg-muted, #9ca3af);
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
      transition: background 120ms, color 120ms;
    }
    .flg-pr-dismiss-btn:hover {
      background: var(--flg-bg-subtle, #f3f4f6);
      color: var(--flg-fg, #111827);
    }
    .flg-pr-btn-primary {
      padding: 9px 20px; border: none; border-radius: 8px; cursor: pointer;
      background: var(--flg-primary, #D63A3A);
      color: #fff; font-size: 13px; font-weight: 600;
      display: flex; align-items: center; gap: 6px;
      transition: background 120ms, transform 80ms;
      white-space: nowrap;
    }
    .flg-pr-btn-primary:hover  { background: var(--flg-primary-hover, #b52f2f); }
    .flg-pr-btn-primary:active { transform: scale(0.97); }
    .flg-pr-btn-ghost {
      padding: 9px 16px; border: 1px solid var(--flg-border, #e5e7eb);
      border-radius: 8px; cursor: pointer; background: transparent;
      color: var(--flg-fg-muted, #6b7280); font-size: 13px; font-weight: 500;
      transition: background 120ms, color 120ms;
      white-space: nowrap;
    }
    .flg-pr-btn-ghost:hover {
      background: var(--flg-bg-subtle, #f3f4f6);
      color: var(--flg-fg, #111827);
    }
    .flg-pr-pill {
      display: inline-flex; align-items: center; gap: 4px;
      font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 20px;
      background: #fef9c3; color: #854d0e; border: 1px solid #fde68a;
    }
  `;
  document.head.appendChild(el);
}

/* ── FieldRow ────────────────────────────────────────────────────────────────── */
function FieldRow({ icon: Icon, label, placeholder }) {
  return (
    <div className="flg-pr-field-row">
      <span className="flg-pr-field-icon"><Icon size={14} /></span>
      <span className="flg-pr-field-label">{label}</span>
      <span className="flg-pr-field-placeholder">{placeholder}</span>
    </div>
  );
}

/* ── SectionLabel ─────────────────────────────────────────────────────────── */
function SectionLabel({ emoji, text }) {
  return (
    <div className="flg-pr-section-label">
      <span>{emoji}</span>
      <span>{text}</span>
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────────────────── */
export default function ProfileReminderPopup({ missingFields, onCompleteNow, onSnooze }) {
  const cardRef = useRef(null);
  useOverlayScrollLock();

  useEffect(() => {
    ensureStyles();
  }, []);

  // Which fields are missing, split by section
  const missingPersonal   = PERSONAL_FIELDS.filter(f => missingFields.includes(f.key));
  const missingEmergency  = EMERGENCY_FIELDS.filter(f => missingFields.includes(f.key));

  const total = missingFields.length;

  return (
    <div className="flg-pr-overlay" role="dialog" aria-modal="true" aria-label="Complete Your Profile">
      <div className="flg-pr-card" ref={cardRef}>

        {/* Header */}
        <div className="flg-pr-header">
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 20 }}>👋</span>
              <span style={{
                fontSize: 17, fontWeight: 700,
                color: "var(--flg-fg, #111827)",
                lineHeight: 1.3,
              }}>
                Complete Your Profile
              </span>
              <span className="flg-pr-pill">
                {total} {total === 1 ? "field" : "fields"} missing
              </span>
            </div>
            <p style={{
              fontSize: 13, color: "var(--flg-fg-muted, #6b7280)",
              lineHeight: 1.6, margin: 0,
            }}>
              Please complete your personal information to help us maintain accurate
              employee records and enable features such as{" "}
              <strong style={{ color: "var(--flg-fg, #374151)" }}>
                Birthday Celebrations
              </strong>,{" "}
              <strong style={{ color: "var(--flg-fg, #374151)" }}>
                Work Anniversaries
              </strong>,{" "}
              <strong style={{ color: "var(--flg-fg, #374151)" }}>
                Employee Directory
              </strong>, and{" "}
              <strong style={{ color: "var(--flg-fg, #374151)" }}>
                Emergency Contact
              </strong>{" "}information.
            </p>
          </div>

          {/* Snooze × button */}
          <button
            className="flg-pr-dismiss-btn"
            onClick={onSnooze}
            title="Remind me later"
            aria-label="Dismiss reminder"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body — missing fields */}
        <div className="flg-pr-body">
          <div style={{
            fontSize: 13, fontWeight: 600,
            color: "var(--flg-fg, #111827)",
            marginTop: 18, marginBottom: 4,
          }}>
            Please complete the following details:
          </div>

          {/* Personal Details section */}
          {missingPersonal.length > 0 && (
            <>
              <SectionLabel emoji="📅" text="Personal Details" />
              {missingPersonal.map(f => (
                <FieldRow key={f.key} icon={f.icon} label={f.label} placeholder={f.placeholder} />
              ))}
            </>
          )}

          {/* Emergency Contact section */}
          {missingEmergency.length > 0 && (
            <>
              <SectionLabel emoji="🚨" text="Emergency Contact" />
              {missingEmergency.map(f => (
                <FieldRow key={f.key} icon={f.icon} label={f.label} placeholder={f.placeholder} />
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flg-pr-footer">
          <button className="flg-pr-btn-ghost" onClick={onSnooze}>
            Remind Me Later
          </button>
          <button className="flg-pr-btn-primary" onClick={onCompleteNow}>
            ✏️ Complete Now
          </button>
        </div>
      </div>
    </div>
  );
}
